export enum CardState {
  CheckEligibility = 'check-eligibility',
  NotEligible = 'not-eligible',
  EligibleWaitlisted = 'eligible-waitlisted',
  EligibleRequiresKYC = 'eligible-requires-kyc',
  OnboardingKYC = 'onboarding-kyc',
  KYCPending = 'kyc-pending',
  KYCRejected = 'kyc-rejected',
  KYCIssue = 'kyc-issue',
  AgreementsRequired = 'agreements-required',
  AccountSuspended = 'account-suspended',
  SessionExpired = 'session-expired',
  FullAccess = 'full-access',
}

export enum KYCStatus {
  NOT_REQUESTED = 'not_requested',
  REQUESTED = 'requested',
  REJECTED = 'rejected',
  RESUBMIT = 'resubmitted',
  ONHOLD = 'onhold',
  PENDING = 'pending',
  REVIEW = 'review',
  INITIATED = 'initiated',
  APPROVED = 'approved',
}

export interface WaitlistEntry {
  walletAddress: string
  tenantID: string
  position: number
  referralCode: string
  referredBy?: string
  referralCount: number
  boostScore: number
  status: string
  joinedAt: string
  promotedAt?: string
}

export type AccountStatus = 'pending' | 'waitlisted' | 'active' | 'frozen' | 'revoked'

export interface CardEligibilityResult {
  isEligible: boolean
  isRequiredKYC: boolean
  kycStatus?: KYCStatus
  rainStatus?: string
  agreementsAccepted?: boolean
  waitlistEntry?: WaitlistEntry
  autoOnboard?: boolean
  fastLane?: boolean
  accountStatus?: AccountStatus
  maxCards?: number
}

// Utility function to determine card state based on KYC status
export function getCardStateFromKYCStatus(result: CardEligibilityResult): CardState {
  if (!result || typeof result.isEligible === 'undefined') {
    return CardState.CheckEligibility
  }

  if (result.accountStatus === 'frozen' || result.accountStatus === 'revoked') {
    return CardState.AccountSuspended
  }

  if (!result.isEligible) {
    return CardState.NotEligible
  }

  if (result.accountStatus === 'waitlisted' && result.autoOnboard !== true && result.fastLane !== true) {
    return CardState.EligibleWaitlisted
  }

  if (!result.kycStatus) {
    return result.isRequiredKYC ? CardState.EligibleRequiresKYC : CardState.FullAccess
  }

  switch (result.kycStatus) {
    case KYCStatus.NOT_REQUESTED:
    case KYCStatus.REQUESTED:
      return CardState.EligibleRequiresKYC // Start KYC Modal

    case KYCStatus.REJECTED:
      return CardState.KYCRejected // Show Rejected screen

    case KYCStatus.RESUBMIT:
    case KYCStatus.ONHOLD:
      return CardState.KYCIssue // Raise issue with Brahma team

    case KYCStatus.PENDING:
    case KYCStatus.REVIEW:
    case KYCStatus.INITIATED:
      return CardState.KYCPending // Show Pending Screen

    case KYCStatus.APPROVED:
      if (result.rainStatus === 'approved') {
        return result.agreementsAccepted ? CardState.FullAccess : CardState.AgreementsRequired
      }
      return CardState.KYCPending

    default:
      return CardState.EligibleRequiresKYC
  }
}

// Brahma Console Cards API Types
export interface CardAuthTokens {
  accessToken: string
  expiresIn: number // Absolute Unix timestamp in milliseconds when tokens expire
  tokenType: string
}

export interface CardAuthRequest {
  eoa: string
  sig: string
  timestamp: number
  version: string
}

export interface CardTokenRefreshRequest {
  address: string
  accessToken?: string
  expiresIn?: number // Absolute Unix timestamp in seconds (for refresh route)
  tokenType?: string
}
